import {useTranslation} from "react-i18next";
import useNotification from "v4/hooks/useNotification";
import {useHistory} from "react-router-dom";
import {routesBase} from "v4/data/appRoutes";
import {generateUrl} from "v4/services/api.service";
import {OAUTH_AUTHORIZE} from "v4/data/apiRoutes";
import useFetch from "v4/hooks/useFetch";
import {useEffect, useMemo} from "react";
import UserService from "api/user/user";

export default function useOAuthAuthorizePage() {
    const {t} = useTranslation();
    const {addInfoNotification, addErrorNotification} = useNotification();

    const {location: {search}, replace} = useHistory();
    const {
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: responseType,
        scope,
    } = useMemo(() => Object.fromEntries(new URLSearchParams(search ?? '')), [search]);

    const [{data, errorData, isLoading, isFinished, isError}, fetchOAuth] = useFetch('json', {ignoreErrorMessages: true});

    useEffect(() => {
        if (!clientId || !redirectUri || !responseType || !scope) {
            addInfoNotification(t('invalid_oauth_request'));
            UserService.logout();
            return replace(routesBase.baseLogin);
        }

        fetchOAuth({
            url: `${process.env.REACT_APP_POL_CORE_URL}${generateUrl(OAUTH_AUTHORIZE, {
                client_id: clientId,
                redirect_uri: redirectUri,
                response_type: responseType,
                scope: scope,
            }, false)}`,
            config: {
                headers: {
                    Authorization: `Bearer ${UserService.getToken()}`,
                }
            }
        })
    }, [addInfoNotification, clientId, fetchOAuth, redirectUri, replace, responseType, scope, t]);

    useEffect(() => {
        if (!isFinished) return;

        if (isError) {
            if (errorData?.type === 'client-error:forbidden') {
                addErrorNotification(errorData.details);
            } else {
                addErrorNotification(t('invalid_oauth_request'));
            }
        }

        if (!data?.data?.redirect_url || isError) return replace(routesBase.baseLogin);

        if (UserService.isSSOOnly()) {
            UserService.logout();
        }

        window.location.href = data.data.redirect_url;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFinished, errorData, data]);

    return {isLoading}
}
